import type { ForqSupabaseClient } from './client';
import {
  normalizeCustomerAgreements,
  normalizeCustomerSearchInput,
  type AddCustomerAttachmentInput,
  type CheckCustomerDuplicatesInput,
  type CreateCustomerAddressInput,
  type CustomerAttachmentRecord,
  type CreateCustomerContactInput,
  type CreateCustomerInput,
  type CustomerAddressRecord,
  type CustomerAgreements,
  type CustomerContactRecord,
  type CustomerDetailRecord,
  type CustomerDuplicateCandidate,
  type CustomerDuplicateReason,
  type CustomerListRecord,
  type CustomerPage,
  type CustomerSearchInput,
  type CustomerStatus,
  type CustomersGateway,
  type UpdateCustomerAddressInput,
  type UpdateCustomerAgreementsInput,
  type UpdateCustomerContactInput,
  type UpdateCustomerInput,
} from '@forq/customers-data';
import { SupabaseOperationError } from './errors';

interface SearchRow {
  readonly id: string;
  readonly customer_number: string;
  readonly display_name: string;
  readonly city: string;
  readonly status: CustomerStatus;
  readonly phone: string;
  readonly email: string;
  readonly vat_id: string;
  readonly contact_count: number | string;
  readonly updated_at: string;
  readonly active_since: string;
  readonly active_until: string | null;
  readonly total_count: number | string;
}

interface DuplicateRow {
  readonly id: string;
  readonly customer_number: string;
  readonly display_name: string;
  readonly city: string;
  readonly status: CustomerStatus;
  readonly score: number | string;
  readonly reasons: readonly CustomerDuplicateReason[];
}

interface DetailPayload {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly kind: 'company' | 'person';
  readonly displayName: string;
  readonly companyName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly city: string;
  readonly status: CustomerStatus;
  readonly phone: string;
  readonly email: string;
  readonly vatId: string;
  readonly taxNumber: string;
  readonly website: string;
  readonly notes: string;
  readonly contactCount: number | string;
  readonly addresses: readonly CustomerAddressRecord[];
  readonly contacts: readonly CustomerContactRecord[];
  readonly agreements?: CustomerAgreements;
  readonly activeSince?: string;
  readonly activeUntil?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function attachmentList(value: unknown): readonly CustomerAttachmentRecord[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError('customers.attachments', 'The database returned an invalid list.');
  }
  return value as readonly CustomerAttachmentRecord[];
}

function attachmentRecord(value: unknown): CustomerAttachmentRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('customers.attachment', 'The attachment has an invalid shape.');
  }
  return value as CustomerAttachmentRecord;
}

function mapDetail(value: unknown): CustomerDetailRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('customers.detail', 'The customer detail has an invalid shape.');
  }
  const row = value as DetailPayload;
  return {
    id: row.id,
    tenantId: row.tenantId,
    number: row.number,
    kind: row.kind,
    displayName: row.displayName,
    companyName: row.companyName ?? '',
    firstName: row.firstName ?? '',
    lastName: row.lastName ?? '',
    city: row.city ?? '',
    status: row.status,
    phone: row.phone ?? '',
    email: row.email ?? '',
    vatId: row.vatId ?? '',
    taxNumber: row.taxNumber ?? '',
    website: row.website ?? '',
    notes: row.notes ?? '',
    contactCount: Number(row.contactCount ?? row.contacts?.length ?? 0),
    addresses: [...(row.addresses ?? [])],
    contacts: [...(row.contacts ?? [])],
    agreements: normalizeCustomerAgreements(row.agreements),
    activeSince: row.activeSince ?? '',
    activeUntil: row.activeUntil ?? null,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

function customerPayload(input: CreateCustomerInput | UpdateCustomerInput): Readonly<Record<string, unknown>> {
  return {
    kind: input.kind,
    companyName: input.companyName ?? '',
    firstName: input.firstName ?? '',
    lastName: input.lastName ?? '',
    vatId: input.vatId ?? '',
    taxNumber: input.taxNumber ?? '',
    email: input.email ?? '',
    phone: input.phone ?? '',
    website: input.website ?? '',
    notes: input.notes ?? '',
    ...('status' in input && input.status ? { status: input.status } : {}),
    ...('billingAddress' in input && input.billingAddress ? { billingAddress: input.billingAddress } : {}),
    ...('primaryContact' in input && input.primaryContact ? { primaryContact: input.primaryContact } : {}),
  };
}

function duplicatePayload(input: CheckCustomerDuplicatesInput): Readonly<Record<string, unknown>> {
  return {
    kind: input.kind,
    companyName: input.companyName ?? '',
    firstName: input.firstName ?? '',
    lastName: input.lastName ?? '',
    vatId: input.vatId ?? '',
    email: input.email ?? '',
    phone: input.phone ?? '',
    postalCode: input.postalCode ?? '',
    city: input.city ?? '',
  };
}

function addressPayload(input: CreateCustomerAddressInput | UpdateCustomerAddressInput): Readonly<Record<string, unknown>> {
  return {
    kind: input.kind,
    label: input.label ?? '',
    street: input.street,
    postalCode: input.postalCode,
    city: input.city,
    countryCode: input.countryCode ?? 'DE',
    primary: Boolean(input.primary),
  };
}

function contactPayload(input: CreateCustomerContactInput | UpdateCustomerContactInput): Readonly<Record<string, unknown>> {
  return {
    salutation: input.salutation ?? '',
    firstName: input.firstName,
    lastName: input.lastName,
    position: input.position ?? '',
    email: input.email ?? '',
    phone: input.phone ?? '',
    mobile: input.mobile ?? '',
    primary: Boolean(input.primary),
    ...('active' in input && input.active !== undefined ? { active: input.active } : {}),
  };
}

export class SupabaseCustomersGateway implements CustomersGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async detail(functionName: string, args: Record<string, unknown>): Promise<CustomerDetailRecord> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`customers.${functionName}`, messageOf(error));
    return mapDetail(data);
  }

  async searchCustomers(input: CustomerSearchInput): Promise<CustomerPage> {
    const normalized = normalizeCustomerSearchInput(input);
    const { data, error } = await this.client.rpc('customer_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_status: normalized.status === 'all' ? null : normalized.status,
      p_bestand_from: normalized.bestandFrom || null,
      p_bestand_to: normalized.bestandTo || null,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('customers.search', messageOf(error));
    const rows = (data ?? []) as SearchRow[];
    const items: readonly CustomerListRecord[] = rows.map((row) => ({
      id: row.id,
      number: row.customer_number,
      displayName: row.display_name,
      city: row.city ?? '',
      status: row.status,
      phone: row.phone ?? '',
      email: row.email ?? '',
      vatId: row.vat_id ?? '',
      contactCount: Number(row.contact_count ?? 0),
      updatedAt: row.updated_at,
      activeSince: row.active_since ?? '',
      activeUntil: row.active_until ?? null,
    }));
    return {
      items,
      total: Number(rows[0]?.total_count ?? 0),
      page: normalized.page,
      pageSize: normalized.pageSize,
    };
  }

  async getCustomer(tenantId: string, customerId: string): Promise<CustomerDetailRecord> {
    return this.detail('customer_get', { p_tenant_id: tenantId, p_customer_id: customerId });
  }

  async findDuplicates(input: CheckCustomerDuplicatesInput): Promise<readonly CustomerDuplicateCandidate[]> {
    const { data, error } = await this.client.rpc('customer_find_duplicates', {
      p_tenant_id: input.tenantId,
      p_payload: duplicatePayload(input),
      p_exclude_customer_id: input.customerId ?? null,
    });
    if (error) throw new SupabaseOperationError('customers.duplicates', messageOf(error));
    return ((data ?? []) as DuplicateRow[]).map((row) => ({
      id: row.id,
      number: row.customer_number,
      displayName: row.display_name,
      city: row.city ?? '',
      status: row.status,
      score: Number(row.score),
      reasons: [...(row.reasons ?? [])],
    }));
  }

  async createCustomer(input: CreateCustomerInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_create', {
      p_tenant_id: input.tenantId,
      p_payload: customerPayload(input),
    });
  }

  async updateCustomer(input: UpdateCustomerInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_update', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_payload: customerPayload(input),
    });
  }

  async createAddress(input: CreateCustomerAddressInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_address_upsert', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_address_id: null,
      p_payload: addressPayload(input),
    });
  }

  async updateAddress(input: UpdateCustomerAddressInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_address_upsert', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_address_id: input.addressId,
      p_payload: addressPayload(input),
    });
  }

  async deleteAddress(tenantId: string, customerId: string, addressId: string): Promise<CustomerDetailRecord> {
    return this.detail('customer_address_delete', {
      p_tenant_id: tenantId,
      p_customer_id: customerId,
      p_address_id: addressId,
    });
  }

  async createContact(input: CreateCustomerContactInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_contact_create', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_payload: contactPayload(input),
    });
  }

  async updateContact(input: UpdateCustomerContactInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_contact_update', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_contact_id: input.contactId,
      p_payload: contactPayload(input),
    });
  }

  async updateAgreements(input: UpdateCustomerAgreementsInput): Promise<CustomerDetailRecord> {
    return this.detail('customer_agreements_save', {
      p_tenant_id: input.tenantId,
      p_customer_id: input.customerId,
      p_agreements: input.agreements,
    });
  }

  async listAttachments(tenantId: string, customerId: string): Promise<readonly CustomerAttachmentRecord[]> {
    const { data, error } = await this.client.rpc('customer_attachment_list', { p_tenant_id: tenantId, p_customer_id: customerId });
    if (error) throw new SupabaseOperationError('customers.attachments', messageOf(error));
    return attachmentList(data ?? []);
  }

  async getAttachment(tenantId: string, attachmentId: string): Promise<CustomerAttachmentRecord> {
    const { data, error } = await this.client.rpc('customer_attachment_get', { p_tenant_id: tenantId, p_attachment_id: attachmentId });
    if (error) throw new SupabaseOperationError('customers.attachment.get', messageOf(error));
    return attachmentRecord(data);
  }

  async addAttachment(input: AddCustomerAttachmentInput): Promise<CustomerAttachmentRecord> {
    const { tenantId, ...rest } = input;
    const { data, error } = await this.client.rpc('customer_attachment_add', { p_tenant_id: tenantId, p_payload: rest });
    if (error) throw new SupabaseOperationError('customers.attachment.add', messageOf(error));
    return attachmentRecord(data);
  }

  async removeAttachment(tenantId: string, attachmentId: string): Promise<void> {
    const { error } = await this.client.rpc('customer_attachment_remove', { p_tenant_id: tenantId, p_attachment_id: attachmentId });
    if (error) throw new SupabaseOperationError('customers.attachment.remove', messageOf(error));
  }
}
